import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Checkbox,
  CircularProgress,
  Snackbar,
  Alert,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import DeleteIcon from "@mui/icons-material/Delete";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { RiAiGenerate2 } from "react-icons/ri";
import ArticleDetailModal from "../Shared/ArticleDetailModal";
import {
  fetchArticles,
  fetchScripts,
  fetchSaveArticles,
  fetchSaveSingleArticle,
  deleteArticle,
  generateScript,
} from "../../services/api";
import { useNavigate } from "react-router-dom";
import { API_ENDPOINTS } from "../../config/api";
import { clearTokens } from "../../utils/auth";

// Utility function for debouncing API calls
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

function ArticlesTable() {
  const [searchText, setSearchText] = useState("");
  const [articles, setArticles] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalArticles, setTotalArticles] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [sortCriteria, setSortCriteria] = useState({
    type: "date",
    value: "Newest",
  });

  // State for the detail modal
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);

  // New state for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [articleToDelete, setArticleToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // New state for fetch articles modal
  const [fetchModalOpen, setFetchModalOpen] = useState(false);
  const [customUrl, setCustomUrl] = useState("");
  const [maxNumber, setMaxNumber] = useState("10");
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchSingleArticle, setFetchSingleArticle] = useState(false);
  const [selectedArticles, setSelectedArticles] = useState([]);
  const [batchScriptLoading, setBatchScriptLoading] = useState(false);

  // New state for batch delete dialog
  const [batchDeleteDialogOpen, setBatchDeleteDialogOpen] = useState(false);
  const [batchDeleteLoading, setBatchDeleteLoading] = useState(false);

  const navigate = useNavigate();

  // Function to check if a URL is likely an article URL
  const isLikelyArticleUrl = (url) => {
    if (!url) return false;

    // Patterns that suggest an article URL
    const articlePatterns = [
      /\/\d{4}\/\d{2}\/\d{2}\//, // Date pattern like /2025/02/27/
      /\/article\//i, // "article" in the path
      /\/news\//i, // "news" in the path
      /\/post\//i, // "post" in the path
      /\/story\//i, // "story" in the path
      /-[a-z0-9]{8,}/i, // Long dash-separated slug
    ];

    return articlePatterns.some((pattern) => pattern.test(url));
  };

  // Debounced search function to reduce unnecessary API calls
  const debouncedSearch = useCallback(
    debounce((page, search) => {
      loadArticles(page, search);
    }, 500),
    []
  );

  // Function to handle authentication errors
  const handleAuthError = (error) => {
    if (
      error.message &&
      (error.message.includes("Authentication required") ||
        error.message.includes("Invalid token") ||
        error.message.includes("Token expired") ||
        error.message.toLowerCase().includes("unauthorized") ||
        error.message.toLowerCase().includes("authentication"))
    ) {
      // Clear tokens and redirect to login
      clearTokens();
      setNotification({
        open: true,
        message: "Your session has expired. Please log in again.",
        severity: "error",
      });
      // Redirect to login after a short delay
      setTimeout(() => navigate("/login"), 2000);
    }
  };

  // Function to load articles from the API
  const loadArticles = async (page = 1, search = "") => {
    setLoading(true);
    setError(null);

    try {
      // Get token from localStorage or fallback to API token
      const accessToken = localStorage.getItem("accessToken");
      const token = accessToken || process.env.REACT_APP_API_TOKEN;

      // Check if a token is available
      if (!token) {
        throw new Error("Authentication required. Please log in.");
      }

      // Construct URL with order_by=-id parameter for latest first
      let url = `${API_ENDPOINTS.ARTICLES}?order_by=-id&page=${page}`;

      // Add search parameter if provided
      if (search) {
        url += `&title=${encodeURIComponent(search)}`;
      }

      // Make direct fetch call to ensure proper parameters
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        const statusText = response.statusText;

        // Try to get error details from the response
        try {
          const errorData = await response.json();
          throw new Error(
            `API request failed: ${
              errorData.detail ||
              errorData.message ||
              statusText ||
              response.status
            }`
          );
        } catch (parseError) {
          // If we can't parse JSON, use the status text
          throw new Error(
            `API request failed with status ${response.status} (${
              statusText || "Unknown error"
            })`
          );
        }
      }

      const articlesData = await response.json();

      // Fetch all pages of scripts to create a complete lookup map of scripted articles
      const scriptsByArticleId = {};

      // First, fetch page 1 to get total pages info
      const firstPageScriptsData = await fetchScripts(1);
      const totalScriptPages = firstPageScriptsData.last_page || 1;

      // Process first page
      if (firstPageScriptsData && firstPageScriptsData.results) {
        firstPageScriptsData.results.forEach((script) => {
          if (script.article && script.script_generated === "generated") {
            scriptsByArticleId[script.article] = script;
          }
        });
      }

      // Fetch remaining pages if there are more than one
      if (totalScriptPages > 1) {
        const remainingPagesPromises = [];
        for (let scriptPage = 2; scriptPage <= totalScriptPages; scriptPage++) {
          remainingPagesPromises.push(fetchScripts(scriptPage));
        }

        // Wait for all remaining pages to be fetched
        const allScriptPages = await Promise.all(remainingPagesPromises);

        // Process each page's results
        allScriptPages.forEach((pageData) => {
          if (pageData && pageData.results) {
            pageData.results.forEach((script) => {
              if (script.article && script.script_generated === "generated") {
                scriptsByArticleId[script.article] = script;
              }
            });
          }
        });
      }

      // Transform API data to match our component's expected structure
      const transformedArticles = articlesData.results.map((article) => {
        // Check if this article has a script by looking in our map
        const hasGeneratedScript = scriptsByArticleId[article.id] !== undefined;

        return {
          id: article.id,
          headline: article.title,
          // Set status based on whether article ID matches a script with generated status
          status: hasGeneratedScript ? "Scripted" : "Inactive",
          date: formatDate(article.created_at),
          originalData: article, // Keep the original data for the detail modal
        };
      });

      // No need to sort client-side as data should already be sorted by the API
      setArticles(transformedArticles);
      setTotalArticles(articlesData.total_items);
      setTotalPages(articlesData.last_page);
      setCurrentPage(articlesData.current_page);

      if (page === 1 && !search) {
        setNotification({
          open: true,
          message: `Successfully fetched ${transformedArticles.length} articles!`,
          severity: "success",
        });
      }
    } catch (err) {
      console.error("Error loading articles:", err);

      // Handle authentication errors
      handleAuthError(err);

      setError("Failed to load articles. Please try again.");
      setNotification({
        open: true,
        message: `Error: ${err.message}`,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Format date from API
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Load initial data and refresh when page or sort criteria changes
  useEffect(() => {
    loadArticles(currentPage, searchText);
  }, [currentPage, sortCriteria]);

  // Debounced search handler
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);

    // If search is empty, reset to first page and fetch all articles
    if (value === "") {
      setCurrentPage(1);
      loadArticles(1);
    } else {
      // Use debounced search to reduce API calls
      debouncedSearch(1, value);
    }
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchText("");
    setCurrentPage(1);
    loadArticles(1);
  };

  // Handle search on Enter key
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      loadArticles(1, searchText);
    }
  };

  const handleSortChange = (sortOption) => {
    setSortCriteria(sortOption);

    // Reset to page 1 when changing sort criteria
    setCurrentPage(1);

    // Load articles with the new sort criteria
    // Date filtering is now handled within loadArticles
    loadArticles(1, searchText);
  };

  // Handler for opening the detail modal
  const handleOpenDetailModal = (article) => {
    // Pass the original API data structure to the modal
    setSelectedArticle(article.originalData);
    setDetailModalOpen(true);
  };

  // Handler for closing the detail modal
  const handleCloseDetailModal = () => {
    setDetailModalOpen(false);
  };

  // Handler to update article status after script generation in the detail modal
  const handleScriptGeneratedFromModal = (articleId) => {
    // Update the status of the article in the local state
    const updatedArticles = articles.map((article) => {
      if (article.id === articleId) {
        return {
          ...article,
          status: "Scripted",
        };
      }
      return article;
    });

    setArticles(updatedArticles);
  };

  // New handler for opening delete confirmation dialog
  const handleOpenDeleteDialog = (article) => {
    setArticleToDelete(article);
    setDeleteDialogOpen(true);
  };

  // New handler for closing delete confirmation dialog
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setArticleToDelete(null);
  };

  // New handler for confirming and executing delete
  const handleConfirmDelete = async () => {
    if (!articleToDelete?.id) return;

    setDeleteLoading(true);
    try {
      await deleteArticle(articleToDelete.id);

      // Close dialog and show success notification
      setDeleteDialogOpen(false);
      setNotification({
        open: true,
        message: "Article deleted successfully",
        severity: "success",
      });

      // Refresh the articles list
      loadArticles(currentPage, searchText);
    } catch (error) {
      setNotification({
        open: true,
        message: "Error deleting article: " + error.message,
        severity: "error",
      });
    } finally {
      setDeleteLoading(false);
      setArticleToDelete(null);
    }
  };

  // New handler for fetching articles from URL
  const handleFetchFromUrl = async () => {
    if (!customUrl) {
      setNotification({
        open: true,
        message: "Please enter a valid URL",
        severity: "error",
      });
      return;
    }

    setFetchLoading(true);

    try {
      let result;

      // Choose appropriate API endpoint based on whether it's a single article
      if (fetchSingleArticle) {
        // Use the single article fetch endpoint
        result = await fetchSaveSingleArticle(customUrl);

        setNotification({
          open: true,
          message: `Successfully fetched article from ${customUrl}!`,
          severity: "success",
        });
      } else {
        // Use the bulk articles fetch endpoint
        const payload = {
          custom_url: customUrl,
          max_number: parseInt(maxNumber, 10),
        };

        result = await fetchSaveArticles(payload);

        setNotification({
          open: true,
          message: `Successfully fetched ${
            result.articles_saved || "multiple"
          } articles from ${customUrl}!`,
          severity: "success",
        });
      }

      // Close the modal and reset fields
      setFetchModalOpen(false);
      setCustomUrl("");
      setMaxNumber("50");
      setFetchSingleArticle(false);

      // Reset search text and sorting to ensure new articles are visible
      setSearchText("");
      setSortCriteria({
        type: "date",
        value: "Newest",
      });
      setCurrentPage(1);

      // Reload the articles list with fresh data
      await loadArticles(1);
    } catch (err) {
      setNotification({
        open: true,
        message: `Error: ${err.message || "Failed to fetch articles"}`,
        severity: "error",
      });
    } finally {
      setFetchLoading(false);
    }
  };

  // Close notification
  const handleCloseNotification = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setNotification({ ...notification, open: false });
  };

  // Helper function to get status-based chip styles
  const getStatusStyles = (status) => {
    if (status === "Scripted") {
      return {
        chip: {
          color: "#008767",
          bgcolor: "#16C09861",
          border: "1.13px solid #00B087",
        },
      };
    } else {
      return {
        chip: {
          color: "#DF0404",
          bgcolor: "#FFC5C5",
          border: "1.13px solid #DF0404",
        },
      };
    }
  };

  // Handle article selection
  const handleArticleSelect = (articleId) => {
    setSelectedArticles((prev) => {
      if (prev.includes(articleId)) {
        return prev.filter((id) => id !== articleId);
      } else {
        return [...prev, articleId];
      }
    });
  };

  // Handle select all articles
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedArticles(articles.map((article) => article.id));
    } else {
      setSelectedArticles([]);
    }
  };

  // Handle batch script generation
  const handleBatchScriptGeneration = async () => {
    if (selectedArticles.length === 0) {
      setNotification({
        open: true,
        message: "Please select at least one article to generate scripts",
        severity: "warning",
      });
      return;
    }

    setBatchScriptLoading(true);
    const errors = [];
    const successes = [];

    try {
      // Generate scripts for all selected articles
      await Promise.all(
        selectedArticles.map(async (articleId) => {
          const article = articles.find((a) => a.id === articleId);
          if (!article) return;

          try {
            const payload = {
              article_id: articleId,
              script_name: article.headline,
            };

            await generateScript(payload);
            successes.push(articleId);
          } catch (error) {
            errors.push(articleId);
          }
        })
      );

      // Update local article statuses without requiring a server fetch
      if (successes.length > 0) {
        // Create updated articles array with new statuses
        const updatedArticles = articles.map((article) => {
          // If this article was successfully scripted, update its status
          if (successes.includes(article.id)) {
            return {
              ...article,
              status: "Scripted",
            };
          }
          return article;
        });

        // Update articles state with new statuses
        setArticles(updatedArticles);
      }

      // Show success/error notifications
      if (successes.length > 0) {
        setNotification({
          open: true,
          message: `Successfully generated scripts for ${
            successes.length
          } article${successes.length > 1 ? "s" : ""}`,
          severity: "success",
        });

        // Redirect to scripts page after a short delay
        setTimeout(() => {
          navigate("/scripts");
        }, 1500);
      }

      if (errors.length > 0) {
        setNotification({
          open: true,
          message: `Failed to generate scripts for ${errors.length} article${
            errors.length > 1 ? "s" : ""
          }`,
          severity: "error",
        });
      }

      // Clear selections
      setSelectedArticles([]);

      // We don't need to reload from the server since we've updated the status locally
      // But we should fetch the latest scripts to keep our data in sync
      if (successes.length > 0) {
        // Only reload articles if there were failures
        if (errors.length > 0) {
          loadArticles(currentPage, searchText);
        }
      }
    } catch (error) {
      setNotification({
        open: true,
        message: "An error occurred while generating scripts",
        severity: "error",
      });

      setBatchScriptLoading(false);
    }
  };

  // New handler for opening batch delete confirmation dialog
  const handleOpenBatchDeleteDialog = () => {
    if (selectedArticles.length === 0) {
      setNotification({
        open: true,
        message: "Please select at least one article to delete",
        severity: "warning",
      });
      return;
    }
    setBatchDeleteDialogOpen(true);
  };

  // New handler for closing batch delete confirmation dialog
  const handleCloseBatchDeleteDialog = () => {
    setBatchDeleteDialogOpen(false);
  };

  // New handler for confirming and executing batch delete
  const handleConfirmBatchDelete = async () => {
    setBatchDeleteLoading(true);
    const errors = [];
    const successes = [];

    try {
      // Delete all selected articles sequentially
      for (const articleId of selectedArticles) {
        try {
          await deleteArticle(articleId);
          successes.push(articleId);
        } catch (error) {
          console.error(`Error deleting article ID ${articleId}:`, error);
          errors.push(articleId);
        }
      }

      // Close dialog
      setBatchDeleteDialogOpen(false);

      // Show success/error notifications
      if (successes.length > 0) {
        setNotification({
          open: true,
          message: `Successfully deleted ${successes.length} article${
            successes.length > 1 ? "s" : ""
          }`,
          severity: "success",
        });
      }

      if (errors.length > 0) {
        setNotification({
          open: true,
          message: `Failed to delete ${errors.length} article${
            errors.length > 1 ? "s" : ""
          }`,
          severity: "error",
        });
      }

      // Clear selections and reload articles
      setSelectedArticles([]);
      loadArticles(currentPage, searchText);
    } catch (error) {
      setNotification({
        open: true,
        message: "An error occurred while deleting articles",
        severity: "error",
      });
    } finally {
      setBatchDeleteLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Top section with heading and search */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          backgroundColor: "#fff",
          mt: "50px",
          padding: "44px",
        }}
      >
        {/* Left side - Title and subtitle */}
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography
            variant="h5"
            sx={{ fontWeight: "bold", fontSize: "25px" }}
          >
            Articles
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "#888", mt: 0.5, textAlign: "left", fontSize: "16px" }}
          >
            All Articles
          </Typography>
        </Box>

        {/* Right side - Search, sort and buttons */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {/* Search field */}
          <Box sx={{ position: "relative", mr: 2 }}>
            <TextField
              size="small"
              placeholder="Search by headline"
              value={searchText}
              onChange={handleSearchChange}
              onKeyPress={handleKeyPress}
              sx={{
                width: 250,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "11.31px",
                  color: "#B5B7C0",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ ml: 1, mr: 1, color: "#7E7E7E", cursor: "pointer" }}
                    />
                  </InputAdornment>
                ),
                endAdornment: searchText && (
                  <InputAdornment position="end">
                    <ClearIcon
                      sx={{ cursor: "pointer", color: "#7E7E7E" }}
                      onClick={handleClearSearch}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Sort component */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mr: "18px",
              ml: 1,
            }}
          >
            {/* <SortBy defaultValue="Newest" onSortChange={handleSortChange} /> */}
          </Box>

          {/* Action buttons */}
          <Button
            variant="contained"
            onClick={handleBatchScriptGeneration}
            disabled={batchScriptLoading || selectedArticles.length === 0}
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              mr: "18px",
              bgcolor: "#378dea",
              height: "42px",
              width: "139px",
              boxShadow: "none",
              padding: "10px",
              color: "#FFFFFF",
              "&:hover": {
                bgcolor: "#1565c0",
              },
              "&:disabled": {
                bgcolor: "#B0C4DE",
                color: "#FFFFFF",
              },
            }}
          >
            {batchScriptLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Generate script"
            )}
          </Button>
          <Button
            variant="contained"
            onClick={handleOpenBatchDeleteDialog}
            disabled={selectedArticles.length === 0}
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              mr: "18px",
              bgcolor: "#DF0404",
              height: "42px",
              width: "139px",
              boxShadow: "none",
              padding: "10px",
              color: "#FFFFFF",
              "&:hover": {
                bgcolor: "#b30000",
              },
              "&:disabled": {
                bgcolor: "#FFC5C5",
                color: "#FFFFFF",
              },
            }}
          >
            Delete selected
          </Button>
          <Button
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              bgcolor: "#323232",
              height: "42px",
              width: "158px",
              padding: "10px",
              boxShadow: "none",
              color: "#FFFFFF",
              "&:hover": {
                bgcolor: "#222",
              },
            }}
            onClick={() => setFetchModalOpen(true)}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Fetch new articles"
            )}
          </Button>
        </Box>
      </Box>

      {/* Error message if any */}
      {error && (
        <Box sx={{ textAlign: "center", color: "error.main", my: 2 }}>
          <Typography variant="body1">{error}</Typography>
        </Box>
      )}

      {/* Table of articles */}
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: "8px",
          boxShadow: "none",
          border: "1px solid #eee",
        }}
      >
        <Table>
          <TableHead>
            <TableRow
              sx={{ backgroundColor: "#fff", borderBottom: "1px solid #eee" }}
            >
              <TableCell
                padding="checkbox"
                sx={{ borderBottom: "1px solid #eee" }}
              >
                <Checkbox
                  size="small"
                  checked={
                    articles.length > 0 &&
                    selectedArticles.length === articles.length
                  }
                  indeterminate={
                    selectedArticles.length > 0 &&
                    selectedArticles.length < articles.length
                  }
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: "bold",
                  borderBottom: "1px solid #eee",
                  color: "#B5B7C0",
                }}
              >
                ID
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: "bold",
                  borderBottom: "1px solid #eee",
                  color: "#B5B7C0",
                }}
              >
                Headline
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: "bold",
                  borderBottom: "1px solid #eee",
                  color: "#B5B7C0",
                }}
              >
                Date
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: "bold",
                  borderBottom: "1px solid #eee",
                  color: "#B5B7C0",
                }}
              >
                Status
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontWeight: "bold",
                  borderBottom: "1px solid #eee",
                  color: "#B5B7C0",
                }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && articles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: "center", py: 3 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : articles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: "center", py: 3 }}>
                  <Typography>No articles found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              articles.map((article) => {
                const statusStyles = getStatusStyles(article.status);

                return (
                  <TableRow
                    key={article.id}
                    sx={{
                      "&:hover": { backgroundColor: "#f5f5f5" },
                      borderBottom: "1px solid #eee",
                    }}
                  >
                    <TableCell
                      padding="checkbox"
                      sx={{ borderBottom: "1px solid #eee" }}
                    >
                      <Checkbox
                        size="small"
                        checked={selectedArticles.includes(article.id)}
                        onChange={() => handleArticleSelect(article.id)}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: "1px solid #eee",
                        color: "#292D32",
                        fontWeight: "bold",
                      }}
                    >
                      {article.id}
                    </TableCell>
                    <TableCell
                      sx={{ borderBottom: "1px solid #eee", color: "#555555" }}
                    >
                      {article.headline}
                    </TableCell>
                    <TableCell sx={{ borderBottom: "1px solid #eee" }}>
                      {article.date}
                    </TableCell>
                    <TableCell sx={{ borderBottom: "1px solid #eee" }}>
                      <Chip
                        label={article.status}
                        variant="filled"
                        sx={{
                          borderRadius: "6px",
                          textTransform: "capitalize",
                          fontFamily: "poppins, sans-serif",
                          fontWeight: "bold",
                          letterSpacing: "-1%",
                          ...statusStyles.chip,
                        }}
                      />
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ borderBottom: "1px solid #eee" }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", md: "row" },
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 1,
                        }}
                      >
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{
                            textTransform: "none",
                            borderRadius: "8px",
                            border: "1px solid #e0e0e0",
                            color: "#666",
                            padding: { xs: "6px", sm: "8px" },
                            fontSize: {
                              xs: "0.75rem",
                              sm: "0.8rem",
                              md: "0.875rem",
                            },
                            minWidth: { xs: "100%", md: "auto" },
                            width: { xs: "100%", md: "auto" },
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: { xs: "4px", sm: "8px" },
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            "&:hover": {
                              bgcolor: "#f5f5f5",
                              border: "1px solid #e0e0e0",
                            },
                          }}
                          onClick={() => handleOpenDetailModal(article)}
                        >
                          <Box sx={{ display: { xs: "none", sm: "inline" } }}>
                            Generate Script
                          </Box>
                          <Box sx={{ display: { xs: "inline", sm: "none" } }}>
                            Script
                          </Box>
                          <RiAiGenerate2
                            style={{
                              fontSize: 18,
                              flexShrink: 0,
                              color: "inherit",
                            }}
                          />
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{
                            textTransform: "none",
                            borderRadius: "8px",
                            border: "1px solid #e0e0e0",
                            color: "#DF0404",
                            padding: "8px",
                            minWidth: { xs: "100%", md: "auto" },
                            "&:hover": {
                              bgcolor: "#fff1f1",
                              border: "1px solid #DF0404",
                            },
                          }}
                          onClick={() => handleOpenDeleteDialog(article)}
                        >
                          Delete
                          <DeleteIcon
                            sx={{
                              width: 20,
                              height: 20,
                              marginLeft: 0.5,
                            }}
                          />
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{
                            textTransform: "none",
                            borderRadius: "8px",
                            border: "1px solid #e0e0e0",
                            color: "#4e68d2",
                            padding: "8px",
                            minWidth: { xs: "100%", md: "auto" },
                            "&:hover": {
                              bgcolor: "#f5f5f5",
                              border: "1px solid #e0e0e0",
                            },
                          }}
                          onClick={() =>
                            window.open(
                              article.originalData.fetched_from,
                              "_blank"
                            )
                          }
                        >
                          <OpenInNewIcon
                            alt="link Icon"
                            style={{
                              width: "20px",
                              height: "20px",
                              marginRight: "4px",
                            }}
                          />
                          Open
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {articles.length > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 2,
          }}
        >
          <Typography variant="body2" sx={{ color: "#666" }}>
            Showing data {totalArticles > 0 ? (currentPage - 1) * 24 + 1 : 0} to{" "}
            {Math.min(currentPage * 24, totalArticles)} of {totalArticles}{" "}
            entries
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography
              component="span"
              sx={{
                mx: 1,
                color: "#999",
                cursor: currentPage === 1 ? "default" : "pointer",
                opacity: currentPage === 1 ? 0.5 : 1,
                fontSize: "14px",
              }}
              onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
            >
              &lt;
            </Typography>
            {Array.from({ length: totalPages }).map((_, index) => (
              <Typography
                key={index}
                component="span"
                sx={{
                  mx: 1,
                  bgcolor: currentPage === index + 1 ? "#4691E1" : "#F5F5F5",
                  color: currentPage === index + 1 ? "white" : "#404B52",
                  borderRadius: "4px",
                  px: 1.5,
                  py: 0.5,
                  fontSize: "14px",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
                onClick={() => setCurrentPage(index + 1)}
              >
                {index + 1}
              </Typography>
            ))}
            <Typography
              component="span"
              sx={{
                mx: 1,
                color: "#999",
                cursor: currentPage === totalPages ? "default" : "pointer",
                opacity: currentPage === totalPages ? 0.5 : 1,
                fontSize: "14px",
              }}
              onClick={() =>
                currentPage < totalPages && setCurrentPage(currentPage + 1)
              }
            >
              &gt;
            </Typography>
          </Box>
        </Box>
      )}

      {/* Detail Modal */}
      <ArticleDetailModal
        open={detailModalOpen}
        handleClose={handleCloseDetailModal}
        article={selectedArticle}
        onScriptGenerated={handleScriptGeneratedFromModal}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: "bold", color: "#DF0404" }}>
          Delete Article
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete article "{articleToDelete?.headline}
            "? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleCloseDeleteDialog}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              border: "1px solid #e0e0e0",
              color: "#666",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            disabled={deleteLoading}
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              bgcolor: "#DF0404",
              height: "42px",
              boxShadow: "none",
              color: "#FFFFFF",
              "&:hover": {
                bgcolor: "#b30000",
              },
            }}
          >
            {deleteLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Delete"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fetch Articles Modal */}
      <Dialog
        open={fetchModalOpen}
        onClose={() => setFetchModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>
          {fetchSingleArticle
            ? "Fetch Single Article"
            : "Fetch Articles from URL"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {fetchSingleArticle
              ? "Enter the URL of the specific article you want to fetch."
              : "Enter the URL from which to fetch multiple articles and the maximum number of articles to fetch."}
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label={fetchSingleArticle ? "Article URL" : "Website URL"}
            type="url"
            fullWidth
            value={customUrl}
            onChange={(e) => {
              const newUrl = e.target.value;
              setCustomUrl(newUrl);

              // Auto-detect if this appears to be an article URL
              if (newUrl && isLikelyArticleUrl(newUrl) && !fetchSingleArticle) {
                setFetchSingleArticle(true);
              }
            }}
            placeholder={
              fetchSingleArticle
                ? "https://www.bbc.com/news/articles/cx29z8lyx71o"
                : "https://techcrunch.com"
            }
            sx={{ mb: 2 }}
          />

          {/* Checkbox for single article */}
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Checkbox
              checked={fetchSingleArticle}
              onChange={(e) => {
                setFetchSingleArticle(e.target.checked);
                // Auto-detect if URL looks like an article URL
                if (
                  e.target.checked &&
                  customUrl &&
                  isLikelyArticleUrl(customUrl)
                ) {
                  setMaxNumber(1);
                }
              }}
              id="fetch-single-article"
            />
            <Typography
              component="label"
              htmlFor="fetch-single-article"
              sx={{ cursor: "pointer" }}
            >
              This is a specific article URL (not a website homepage)
            </Typography>
          </Box>

          {!fetchSingleArticle && (
            <TextField
              margin="dense"
              label="Maximum Number of Articles"
              type="number"
              fullWidth
              value={maxNumber}
              onChange={(e) => {
                const val = e.target.value; // this is always a string
                if (val === "") {
                  // let them erase everything
                  setMaxNumber("");
                } else {
                  // only accept real numbers
                  const num = parseInt(val, 10);
                  if (!isNaN(num) && num > 0) {
                    setMaxNumber(num.toString());
                  }
                }
              }}
              inputProps={{ min: 1 }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setFetchModalOpen(false)}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              border: "1px solid #e0e0e0",
              color: "#666",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleFetchFromUrl}
            disabled={fetchLoading || !customUrl}
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              bgcolor: "#323232",
              height: "42px",
              boxShadow: "none",
              color: "#FFFFFF",
              "&:hover": {
                bgcolor: "#222",
              },
            }}
          >
            {fetchLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : fetchSingleArticle ? (
              "Fetch Article"
            ) : (
              "Fetch Articles"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Batch Delete Confirmation Dialog */}
      <Dialog
        open={batchDeleteDialogOpen}
        onClose={handleCloseBatchDeleteDialog}
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: "bold", color: "#DF0404" }}>
          Delete Selected Articles
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {selectedArticles.length} selected
            article
            {selectedArticles.length > 1 ? "s" : ""}? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleCloseBatchDeleteDialog}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              border: "1px solid #e0e0e0",
              color: "#666",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmBatchDelete}
            disabled={batchDeleteLoading}
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              bgcolor: "#DF0404",
              height: "42px",
              boxShadow: "none",
              color: "#FFFFFF",
              "&:hover": {
                bgcolor: "#b30000",
              },
            }}
          >
            {batchDeleteLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Delete"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ArticlesTable;
